import { type Ref, createApp, type App, defineComponent, ref, computed, unref } from 'vue-demi'
import { type ExperienceActivationPayload } from '../../../../../../types/models'
import { type IVueActivationTargetRenderer } from './activation-target-renderer'
import { ActivationPreviewListGrid } from '@source-digital/activation-list-vue'
import { BaseVueActivationTargetRenderer } from './base'
import { type ExtractProps } from '../../../../../../types/helper'
import { applyInlineStyles } from '../../../../../../utils/dom-style'
import { setupOverlay } from '../../../../../../utils/overlay'
import { type IDetailsOverlayTarget } from './details-overlay-target'
import { type UseElementBoundingReturn } from '@vueuse/core'
import { useElementBoundingPatch } from '../utils/patches/use-element-bounding-patch'
import { useSingleElementResizeObserver } from '../utils/patches/use-resize-observer-patch'
import { debounce } from 'lodash-es'
import { useActivationPreviewListGrid } from '../utils/use-activation-preview-list-grid'

const MEDIA_MARKER_AREA_NAME = 'main'

export class VueActivationTargetOuterOverlayRenderer extends BaseVueActivationTargetRenderer implements IVueActivationTargetRenderer, IDetailsOverlayTarget {
  itemsMap: Ref<Record<string, ExperienceActivationPayload[] | undefined>>
  renderTargetEl: HTMLElement
  vueApp: App
  vueInstance: Vue
  #overlayWatch: () => void
  #areaElBounding: Record<string, UseElementBoundingReturn> = {}
  #gridElRef: Ref<InstanceType<typeof ActivationPreviewListGrid> | undefined>
  #onVideoAreaResize: (entry?: ResizeObserverEntry, transformStyle?: Record<string, any>) => void = () => {}
  constructor (itemsMap: Ref<Record<string, ExperienceActivationPayload[] | undefined>>, ...args: ConstructorParameters<typeof BaseVueActivationTargetRenderer>) {
    super(...args)
    this.itemsMap = itemsMap
  }

  getElementBounding (name?: string): UseElementBoundingReturn | undefined {
    if (name == null) return undefined
    if (this.#areaElBounding[name] == null) {
      this.#areaElBounding[name] = useElementBoundingPatch(() => this.#gridElRef.value?.findElementByArea(name) as HTMLElement ?? undefined)
    }
    return this.#areaElBounding[name]
  }

  onVideoAreaResize (fn: (entry?: ResizeObserverEntry, transformStyle?: Record<string, any>) => void) {
    this.#onVideoAreaResize = fn
  }

  #handleVideoResize (size?: ResizeObserverEntry) {
    const mediaEl = this.ctx.targetEl
    const {
      transformOrigin,
      transitionTimingFunction,
      transitionDuration,
      transitionDelay,
      transitionProperty,
      enableTranslateX = false,
      enableTranslateY = false
    } = this.currentLayout.value.targets?.outerOverlay ?? {}
    const transformStyle: Record<string, any> = {}
    if (transformOrigin != null) transformStyle['transform-origin'] = transformOrigin
    if (transitionTimingFunction != null) transformStyle['transition-timing-function'] = transitionTimingFunction
    if (transitionDuration != null) transformStyle['transition-duration'] = transitionDuration
    if (transitionDelay != null) transformStyle['transition-delay'] = transitionDelay
    transformStyle['transition-property'] = transitionProperty ?? 'transform'
    const scale = Math.min(1, ((size?.contentRect.width ?? 0) / mediaEl.offsetWidth), ((size?.contentRect.height ?? 0) / mediaEl.offsetHeight))
    const transform: string[] = []
    if (enableTranslateX !== false) {
      let offsetLeft = (size?.target as HTMLElement)?.offsetLeft ?? 0
      if (enableTranslateX === 'center') offsetLeft += (size?.target as HTMLElement)?.offsetWidth / 2 ?? 0
      transform.push(`translateX(${offsetLeft}px)`)
    }
    if (enableTranslateY !== false) {
      let offsetTop = (size?.target as HTMLElement)?.offsetTop ?? 0
      if (enableTranslateY === 'center') offsetTop += (size?.target as HTMLElement)?.offsetHeight / 2 ?? 0
      transform.push(`translateY(${offsetTop}px)`)
    }
    transform.push(`scale(${scale})`)
    transformStyle.transform = transform.join(' ')
    this.#onVideoAreaResize(size, transformStyle)
  }

  async initialize () {
    const wrapperEl = this.createElement('div', undefined, { role: 'outer-overlay' })
    applyInlineStyles({ 'pointer-events': 'none', ...(this.ctx.settings?.direct?.options?.overlayContainerStyle ?? {}) }, wrapperEl)
    this.renderTargetEl = this.createElement('div')
    wrapperEl.appendChild(this.renderTargetEl)
    this.#overlayWatch = setupOverlay(this.ctx.targetEl, wrapperEl, { useOffset: true })
    // setup float
    this.vueApp = createApp(this.#createComponent())
    this.ctx.targetEl.after(wrapperEl)
    this.vueInstance = this.vueApp.mount(this.renderTargetEl)
  }

  #getDetailsGridStyle () {
    if (!this.detailsRenderer.isOpen.value) return {}
    const detailsTarget = this.currentLayout.value.details?.target
    const mode = detailsTarget?.mode
    if (mode === 'outer-overlay-area') return detailsTarget?.grid ?? {}
    if (mode === 'in-place') {
      const { position } = this.detailsRenderer.currentActivationPosition.value ?? {}
      const outerOverlayOption = detailsTarget?.targets?.outerOverlay
      const fallback = outerOverlayOption?.grid ?? {}
      if (position == null) return fallback
      return outerOverlayOption?.positions?.[position]?.grid ?? fallback
    }
    return {}
  }

  #createComponent () {
    this.#gridElRef = ref()
    const itemsMap = computed(() => this.detailsRenderer.isOpen.value ? {} : this.itemsMap.value)
    const attrs = this.ctx.getComponentMarkerAttributes('outer-overlay')
    const grid = computed(() => {
      return {
        area: [],
        ...(this.currentLayout.value?.targets?.outerOverlay?.grid ?? {}),
        ...this.#getDetailsGridStyle()
      }
    })
    useSingleElementResizeObserver(
      // @ts-expect-error allow empty
      () => this.#gridElRef?.value?.$el?.querySelector(`.sdv-grid-area__${MEDIA_MARKER_AREA_NAME}`),
      debounce((entries: ResizeObserverEntry[]) => {
        this.#handleVideoResize(entries?.[0])
      }, 200))
    const {
      getListStyle,
      getOptions
    } = useActivationPreviewListGrid(this.currentLayout, 'outerOverlay')
    return defineComponent({
      name: 'EmbedderActivationTargetOuterOverlay',
      provide: this.createProvideObject(),
      attrs,
      setup: () => ({
        gridEl: this.#gridElRef
      }),
      render: h => {
        if (this.hide.value) return h()
        return h(ActivationPreviewListGrid, {
          ref: 'gridEl',
          class: unref(this.rootClass) + ' sdv-grid-outer-overlay',
          props: {
            grid: grid.value,
            itemsMap: itemsMap.value,
            animation: this.currentLayout.value?.targets?.outerOverlay?.animation ?? this.directSettings.options?.animation,
            listStyle: getListStyle,
            options: getOptions
          } satisfies ExtractProps<typeof ActivationPreviewListGrid>,
          style: {
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            ...(this.currentLayout.value?.targets?.outerOverlay?.style ?? {})
          },
          on: {
            ...this.createDefaultListeners()
          }
        })
      }
    })
  }

  destroy () {
    if (this.vueApp != null) this.vueApp?.unmount?.()
    this.#overlayWatch?.()
  }
}
